import AppCardTabList from '@nxsys/components/AppCardTabListContainer';
import { ParagraphWithHighlight } from '@nxsys/components/AppConfirmationModal/index.styled';
import AppConfirmationModalWithIcon from '@nxsys/components/AppConfirmationModalWithIcon';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import { OptionType } from '@nxsys/components/AppSelectSync';
import AppTable from '@nxsys/components/AppTableContainer';
import { useDebounce } from '@nxsys/hooks/useDebounce';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { hmrcUpdatesActions } from '@nxsys/store/slices/hmrcUpdates/reducer/hmrcUpdatesSlice';
import { GetHMRCP6p9UpdatesParams } from '@nxsys/store/slices/hmrcUpdates/types';
import {
  HMRCDataType,
  P6p9RecordType,
} from '@nxsys/types/models/hmrcUpdates/p6p9';
import { showToast } from '@nxsys/utils/toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from '../components/ActionsHeader';
import { HMRCUpdateFilterHeader } from '../components/FilterHeader';
import {
  getP6P9AppliedCancelledColumns,
  getP6P9PendingColumns,
} from './columns';
import ModalSelectEmployee from '../components/ModalSelectEmployee';
import { resolveFilterCompanyIds } from '@nxsys/utils/companyScope';

type TabKey = 'pending' | 'appliedCancelled';

type P6P9FilterType = {
  companyRefs: OptionType | null;
  employeeRefs: OptionType | null;
  taxYear: string | null;
  issueDateFrom: string | null;
  issueDateTo: string | null;
  startDate: string | null;
  endDate: string | null;
  effectiveDateFrom: string | null;
  effectiveDateTo: string | null;
  NINumber: string | null;
};

export const P6P9Updates = () => {
  const { messages, formatMessage } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { isBureauEmployer, user, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { p6p9, loading } = useSelector(
    (state: AppState) => state.hmrcUpdatesStore,
  );

  const [activeTab, setActiveTab] = useState<TabKey>('pending');
  const [checkBoxHeader, setCheckBoxHeader] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isOpenApplyModal, setIsOpenApplyModal] = useState(false);
  const [isOpenCancelModal, setIsOpenCancelModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<P6p9RecordType | null>(
    null,
  );
  const [isOpenApplyAllModal, setIsOpenApplyAllModal] = useState(false);
  const [isOpenCancelAllModal, setIsOpenCancelAllModal] = useState(false);
  const [isOpenSelectEmployeeModal, setIsOpenSelectEmployeeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; fullName?: string; forename?: string; surname?: string } | null>(null);

  const [filter, setFilter] = useState<P6P9FilterType>({
    companyRefs: null,
    employeeRefs: null,
    taxYear: null,
    issueDateFrom: null,
    issueDateTo: null,
    startDate: null,
    endDate: null,
    effectiveDateFrom: null,
    effectiveDateTo: null,
    NINumber: null,
  });

  const debouncedSearchTerm = useDebounce(searchValue, 500);

  const tabList: Array<{ key: TabKey; tab: string }> = [
    {
      tab: `${messages['hmrcUpdates.p6p9.pending']}`,
      key: 'pending',
    },
    {
      tab: `${messages['hmrcUpdates.p6p9.appliedCancelled']}`,
      key: 'appliedCancelled',
    },
  ];

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      companyPaymentId: filter.companyRefs?.value || undefined,
      subcontractorId: filter.employeeRefs?.value || undefined,
      issueDateFrom: filter.issueDateFrom || undefined,
      issueDateTo: filter.issueDateTo || undefined,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
      effectiveDateFrom: filter.effectiveDateFrom || undefined,
      effectiveDateTo: filter.effectiveDateTo || undefined,
      NINumber: filter.NINumber || undefined,
      search: debouncedSearchTerm,
    });
  }, [activeTab, filter, debouncedSearchTerm]);

  const handleFetchData = (params: GetHMRCP6p9UpdatesParams) => {
    const newParams = {
      ...params,
      companyPaymentId: params.companyPaymentId || undefined,
      subcontractorId: params.subcontractorId || undefined,
      issueDateFrom: params.issueDateFrom || undefined,
      issueDateTo: params.issueDateTo || undefined,
      startDate: params.startDate || undefined,
      endDate: params.endDate || undefined,
      effectiveDateFrom: params.effectiveDateFrom || undefined,
      effectiveDateTo: params.effectiveDateTo || undefined,
      search: params.search || undefined,
    };

    // The backend filters on companyRefs first (companyPaymentId is its deprecated
    // fallback), so the picked company has to travel as companyRefs to take effect
    newParams.companyRefs = resolveFilterCompanyIds(
      newParams.companyPaymentId ? [newParams.companyPaymentId] : undefined,
      isBureauEmployer,
      user?.companyUserIds,
    );

    if (activeTab === 'pending') {
      dispatch(hmrcUpdatesActions.getHMRCP6p9Pending(newParams));
    } else {
      dispatch(hmrcUpdatesActions.getHMRCP6p9AppliedCancelled(newParams));
    }
  };

  const handleRefresh = () => {
    handleFetchData({
      page: 0,
      size: 20,
      companyPaymentId: filter.companyRefs?.value || undefined,
      subcontractorId: filter.employeeRefs?.value || undefined,
      issueDateFrom: filter.issueDateFrom || undefined,
      issueDateTo: filter.issueDateTo || undefined,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
      effectiveDateFrom: filter.effectiveDateFrom || undefined,
      effectiveDateTo: filter.effectiveDateTo || undefined,
      NINumber: filter.NINumber || undefined,
      search: debouncedSearchTerm,
    });
  };

  const onTabChange = (key: TabKey) => {
    setActiveTab(key);
  };

  const renderData = useMemo(() => {
    return p6p9[activeTab];
  }, [p6p9, activeTab]);

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    console.log(sorter);
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== renderData.pagination.pageSize;
    const shouldResetPage =
      isPageSizeChanged && renderData.pagination.current > 0;
    handleFetchData({
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
      companyPaymentId: filter.companyRefs?.value || undefined,
      subcontractorId: filter.employeeRefs?.value || undefined,
      issueDateFrom: filter.issueDateFrom || undefined,
      issueDateTo: filter.issueDateTo || undefined,
      startDate: filter.startDate || undefined,
      endDate: filter.endDate || undefined,
      effectiveDateFrom: filter.effectiveDateFrom || undefined,
      effectiveDateTo: filter.effectiveDateTo || undefined,
      NINumber: filter.NINumber || undefined,
      search: debouncedSearchTerm,
    });
  };

  const handleSelectAllRow = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCheckBoxHeader(checked);
    if (checked) {
      setSelectedRowIds(
        (renderData.list as P6p9RecordType[]).map(
          (item: P6p9RecordType) => item.id,
        ),
      );
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedRowIds((prev) => [...prev, id]);
    } else {
      setSelectedRowIds((prev) => prev.filter((rowId) => rowId !== id));
      setCheckBoxHeader(false);
    }
  };

  const handleCompanyRefsChange = (value: OptionType | null) => {
    setFilter({ ...filter, companyRefs: value });
  };

  const handleEmployeeRefsChange = (value: OptionType | null) => {
    setFilter({ ...filter, employeeRefs: value });
  };

  const handleIssueDateFromChange = (value: string | null) => {
    setFilter({ ...filter, issueDateFrom: value, issueDateTo: value });
  };

  const handleIssueDateToChange = (value: string | null) => {
    setFilter({ ...filter, issueDateTo: value });
  };

  const handleStartDateChange = (value: string | null) => {
    setFilter({ ...filter, startDate: value });
  };

  const handleEndDateChange = (value: string | null) => {
    setFilter({ ...filter, endDate: value });
  };

  const handleEffectiveDateFromChange = (value: string | null) => {
    setFilter({ ...filter, effectiveDateFrom: value });
  };

  const handleEffectiveDateToChange = (value: string | null) => {
    setFilter({ ...filter, effectiveDateTo: value });
  };

  const handleNINumberChange = (value: string | null) => {
    setFilter({ ...filter, NINumber: value });
  };

  const handleApply = () => {
    if (selectedRowIds.length === 0) {
      showToast({ type: 'warning', message: messages['common.noSelected'] });
      return;
    }
    onApplyRecord(selectedRowIds);
  };

  const handleCancel = () => {
    if (selectedRowIds.length === 0) {
      showToast({ type: 'warning', message: messages['common.noSelected'] });
      return;
    }
    onCancelRecord(selectedRowIds);
  };

  const onApplyRecord = async (ids: number[]) => {
    setIsOpenApplyAllModal(false);
    await dispatch(hmrcUpdatesActions.applyHMRCP6p9({ ids }));
    setSelectedRowIds([]);
    setCheckBoxHeader(false);
    handleRefresh();
  };

  const onCancelRecord = async (ids: number[]) => {
    setIsOpenCancelAllModal(false);
    await dispatch(hmrcUpdatesActions.cancelHMRCP6p9({ ids }));
    setSelectedRowIds([]);
    setCheckBoxHeader(false);
    handleRefresh();
  };

  const handleApplyItem = (record: P6p9RecordType) => {
    setSelectedRecord(record);
    // Skip employee selection modal if record already has a linked employee
    if (record.subcontractorRef?.id) {
      setSelectedEmployee(record.subcontractorRef);
      setIsOpenApplyModal(true);
    } else {
      setIsOpenSelectEmployeeModal(true);
    }
  };

  const handleEmployeeSelected = (employee: { id: string; fullName?: string; forename?: string; surname?: string }) => {
    setSelectedEmployee(employee);
    setIsOpenSelectEmployeeModal(false);
    setIsOpenApplyModal(true);
  };

  const handleCancelItem = async (record: P6p9RecordType) => {
    setSelectedRecord(record);
    setIsOpenCancelModal(true);
  };

  const handleSync = async () => {
    await dispatch(
      hmrcUpdatesActions.hmrcSync({ hmrcDataType: HMRCDataType.P6P9 }),
    );
    handleRefresh();
  };

  const toggleApplyAllModal = () => {
    if (selectedRowIds.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.noSelected'],
      });
      return;
    }
    setIsOpenApplyAllModal(!isOpenApplyAllModal);
  };
  const toggleCancelAllModal = () => {
    if (selectedRowIds.length === 0) {
      showToast({
        type: 'warning',
        message: messages['common.noSelected'],
      });
      return;
    }
    setIsOpenCancelAllModal(!isOpenCancelAllModal);
  };

  const renderColumns = useMemo(() => {
    if (activeTab === 'pending') {
      return getP6P9PendingColumns({
        handleApply: handleApplyItem,
        handleCancel: handleCancelItem,
        messages,
        checkBoxHeader,
        handleSelectAllRow,
        handleSelectRow,
        selectedRowIds,
      });
    }
    return getP6P9AppliedCancelledColumns({
      messages,
      isAgency,
      handleApply: handleApplyItem,
      handleCancel: handleCancelItem,
    });
  }, [
    activeTab,
    checkBoxHeader,
    handleSelectAllRow,
    handleSelectRow,
    selectedRowIds,
    onApplyRecord,
    onCancelRecord,
    isAgency,
  ]);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['hmrc.title']}
        separator=" - "
        title={messages['sidebar.app.hmrcUpdate.p6p9']}
      />
    );
  }, [messages]);

  const setRowClassName = useCallback((record: P6p9RecordType) => {
    if (record?.isWarning) {
      return 'table-row__warning';
    }
    // if (record?.isError) {
    //   return 'table-row__error';
    // }
    return '';
  }, []);

  const isFiltering =
    !!renderData.queryParams?.search ||
    !!renderData.queryParams?.companyPaymentId ||
    !!renderData.queryParams?.subcontractorId ||
    !!renderData.queryParams?.issueDateFrom ||
    !!renderData.queryParams?.issueDateTo ||
    !!renderData.queryParams?.startDate ||
    !!renderData.queryParams?.endDate ||
    !!renderData.queryParams?.effectiveDateFrom ||
    !!renderData.queryParams?.effectiveDateTo ||
    !!renderData.queryParams?.NINumber;

  return (
    <AppsContainer fullView type="bottom" title={renderTitle()}>
      <HMRCUpdateFilterHeader
        companyRefs={filter.companyRefs}
        onCompanyRefsChange={handleCompanyRefsChange}
        employeeRefs={filter.employeeRefs}
        onEmployeeRefsChange={handleEmployeeRefsChange}
        issueDateFrom={filter.issueDateFrom}
        onIssueDateFromChange={handleIssueDateFromChange}
        issueDateTo={filter.issueDateTo}
        onIssueDateToChange={handleIssueDateToChange}
        startDate={filter.startDate}
        onStartDateChange={handleStartDateChange}
        endDate={filter.endDate}
        onEndDateChange={handleEndDateChange}
        effectiveDateFrom={filter.effectiveDateFrom}
        onEffectiveDateFromChange={handleEffectiveDateFromChange}
        effectiveDateTo={filter.effectiveDateTo}
        onEffectiveDateToChange={handleEffectiveDateToChange}
        NINumber={filter.NINumber}
        onNINumberChange={handleNINumberChange}
        isP6P9
      />
      <AppCardTabList
        tabList={tabList}
        activeTab={activeTab}
        handleTabChange={onTabChange}
      >
        <AppsHeader>
          <ActionsHeader
            onApply={toggleApplyAllModal}
            onCancel={toggleCancelAllModal}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            isShowActions={activeTab === 'pending'}
            onSync={handleSync}
          />
        </AppsHeader>
        <AppsContent>
          <AppTable
            data={renderData.list || []}
            loading={loading}
            page={renderData.pagination.current}
            total={renderData.pagination.totalDocs}
            pageSize={renderData.pagination.pageSize}
            scroll={{ x: activeTab === 'pending' ? 2500 : 4000, y: 700 }}
            titleEmpty={messages[`hmrcUpdates.p6p9.empty.${activeTab}`]}
            columns={renderColumns}
            onChangeTable={onChangeTable}
            onChangePagination={onChangePagination}
            rowClassName={setRowClassName}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCardTabList>
      <AppConfirmationModalWithIcon
        title={messages['hmrc.p6p9.apply.title']}
        paragraph={formatMessage(
          { id: 'hmrc.p6p9.apply.confirm' },
          {
            employeeName: (
              <ParagraphWithHighlight>
                {selectedEmployee?.fullName || `${selectedEmployee?.forename || ''} ${selectedEmployee?.surname || ''}`.trim()}
              </ParagraphWithHighlight>
            ),
          },
        )}
        open={isOpenApplyModal}
        type="success"
        icon="IconCheck"
        onConfirm={async () => {
          if (selectedRecord?.id && selectedEmployee?.id) {
            await dispatch(
              hmrcUpdatesActions.applySingleHMRCP6p9({
                id: selectedRecord.id,
                employeeId: selectedEmployee.id,
              }),
            );
            handleRefresh();
          }
          setIsOpenApplyModal(false);
          setSelectedRecord(null);
          setSelectedEmployee(null);
        }}
        onDeny={() => {
          setIsOpenApplyModal(false);
          setSelectedRecord(null);
          setSelectedEmployee(null);
        }}
      />
      <AppConfirmationModalWithIcon
        title={messages['hmrc.p6p9.cancel.title']}
        paragraph={formatMessage(
          { id: 'hmrc.p6p9.cancel.confirm' },
          {
            employeeName: (
              <ParagraphWithHighlight>
                {`${selectedRecord?.subcontractorRef?.forename} ${selectedRecord?.subcontractorRef?.surname}`}
              </ParagraphWithHighlight>
            ),
          },
        )}
        open={isOpenCancelModal}
        type="error"
        icon="IconClose"
        onConfirm={() => {
          if (selectedRecord?.id) {
            onCancelRecord([selectedRecord?.id]);
          }
          setIsOpenCancelModal(false);
          setSelectedRecord(null);
        }}
        onDeny={() => {
          setIsOpenCancelModal(false);
          setSelectedRecord(null);
        }}
      />
      <AppConfirmationModalWithIcon
        title={messages['hmrc.p6p9.apply.title']}
        paragraph={messages['hmrc.p6p9.apply.selected.confirm']}
        open={isOpenApplyAllModal}
        type="success"
        icon="IconCheck"
        onConfirm={handleApply}
        onDeny={() => {
          setIsOpenApplyAllModal(false);
        }}
      />
      <AppConfirmationModalWithIcon
        title={messages['hmrc.p6p9.cancel.title']}
        paragraph={messages['hmrc.p6p9.cancel.selected.confirm']}
        open={isOpenCancelAllModal}
        type="error"
        icon="IconClose"
        onConfirm={handleCancel}
        onDeny={() => {
          setIsOpenCancelAllModal(false);
        }}
      />
      <ModalSelectEmployee
        isOpen={isOpenSelectEmployeeModal}
        toggle={() => {
          setIsOpenSelectEmployeeModal(false);
          setSelectedRecord(null);
        }}
        type="P6P9"
        onConfirm={handleEmployeeSelected}
      />
    </AppsContainer>
  );
};
